/**
 * ChartFromSpec — single spec-driven renderer used by ANY chart that takes
 * its config as JSON. Originally extracted from AgentChart so the AI
 * agent's charts share styling with the bespoke dashboard charts, but
 * available for any caller (saved scenarios, custom dashboards, etc.).
 *
 * Spec format (compact, callable from anything that emits JSON — Claude,
 * a saved-chart store, a future no-code chart builder):
 *
 *   {
 *     "type":    "line" | "bar" | "area" | "pie",
 *     "title":    string?      // caller usually renders the title — kept here
 *                              // for completeness; ChartFromSpec ignores it
 *     "series":  [ ... ],
 *     "x_axis":   { "label": "...", "type": "time" | "category" | "number" }?,
 *     "y_axis":   { "label": "...", "format": <key of Y_FORMATTERS> }?,
 *     "stacked":  bool?,    // line/area/bar
 *     "height":   number?,  // default 300
 *   }
 *
 * Line/bar/area series:
 *   { "name": "Total", "data": [{x, y}, ...], "color"?: "#hex" }
 * Pie series (only one allowed):
 *   { "data": [{label, value, color?}, ...] }
 *
 * Styling pulls from `shared.js` (CHART_PALETTE, Y_FORMATTERS,
 * tickLabelStyle, defaultLegendProps, CHART_MARGINS_SIMPLE) so every chart
 * — agent or bespoke — looks consistent.
 */

import React, { useMemo } from 'react';
import { Alert, Box } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart }  from '@mui/x-charts/BarChart';
import { PieChart }  from '@mui/x-charts/PieChart';

import {
  CHART_PALETTE,
  Y_FORMATTERS,
  tickLabelStyle,
  defaultLegendProps,
  defaultLineLegendProps,
} from './shared';


export default function ChartFromSpec({ spec }) {
  // Defensive parse — bad JSON / wrong shape should yield a small warning,
  // not crash the parent. Same pattern AgentChart already used.
  const parsed = useMemo(() => {
    try {
      return typeof spec === 'string' ? JSON.parse(spec) : spec;
    } catch (e) {
      return { _error: e.message };
    }
  }, [spec]);

  if (parsed?._error) return <SpecFallback raw={spec} error={parsed._error} />;

  const { type, series: rawSeries, x_axis, y_axis, stacked, height } = parsed || {};
  if (!type || !Array.isArray(rawSeries) || rawSeries.length === 0) {
    return <SpecFallback raw={spec} error="Empty or malformed chart spec — missing `type` or `series`" />;
  }

  // Normalise the series: accept a few shapes the model commonly emits.
  //   1. data: [{x, y}, ...]           — canonical
  //   2. data: [1, 2, 3, ...]          — raw numbers, infer x = index+1
  //   3. data: [{label, value}, ...]   — pie shape leaking into non-pie
  //   4. data: [[x, y], ...]           — tuple form
  // Anything else falls through to the error block with debug.
  const series = rawSeries.map((s) => ({
    ...s,
    data: normaliseSeriesData(s?.data, type),
  }));

  // If every series ended up empty after normalisation we've got bad data —
  // show what Claude actually sent so the user (or you) can see why.
  const totalPoints = series.reduce((n, s) => n + (s.data?.length || 0), 0);
  if (totalPoints === 0) {
    return <SpecFallback raw={spec} error="Chart spec has no data points — the series are empty or in an unrecognised shape" />;
  }

  const yFmt = Y_FORMATTERS[y_axis?.format] || Y_FORMATTERS.number;
  const h    = height || 300;

  try {
    if (type === 'pie') {
      return renderPie(series[0], yFmt, h);
    }
    if (type === 'line' || type === 'area') {
      return renderLineOrArea(series, x_axis, yFmt, type === 'area', !!stacked, h);
    }
    if (type === 'bar') {
      return renderBar(series, x_axis, yFmt, !!stacked, h);
    }
    return <SpecFallback raw={spec} error={`Unsupported chart type: ${type}`} />;
  } catch (e) {
    return <SpecFallback raw={spec} error={`Render error: ${e.message}`} />;
  }
}


// ── Renderers ────────────────────────────────────────────────────────────────

function renderLineOrArea(seriesIn, xAxis, yFmt, isArea, stacked, height) {
  const xKind = inferXKind(seriesIn, xAxis);
  const xSet = new Set();
  for (const s of seriesIn) for (const p of (s.data || [])) xSet.add(p.x);
  const xs = sortByKind([...xSet], xKind);

  // MUI X-Charts needs the right TYPE in xAxis.data for each scaleType:
  //   • 'time'   → Date objects (or numeric timestamps), NOT strings
  //   • 'linear' → numbers
  //   • 'point'/'band' → strings or numbers (treated as discrete)
  // Earlier we passed strings to scaleType='time' and the chart silently
  // dropped every datum — that was the "empty line over a populated
  // y-axis" bug. Now: coerce here, and fall back to 'point' if any value
  // fails to parse so the chart still renders.
  let xAxisData, scaleType, valueFormatter;
  if (xKind === 'time') {
    const dates = xs.map((v) => new Date(v));
    if (dates.every((d) => !Number.isNaN(d.getTime()))) {
      xAxisData      = dates;
      scaleType      = 'point';   // 'point' renders cleanly for evenly-spaced dailies / monthlies;
                                   // 'time' adds proportional spacing but is finicky on string→Date
                                   // coercion. We pre-format labels so the visual matches either way.
      valueFormatter = formatTimeX(xs);
    } else {
      // Some entries unparseable — bail to category scale with the raw strings.
      xAxisData      = xs;
      scaleType      = 'point';
      valueFormatter = undefined;
    }
  } else if (xKind === 'number') {
    xAxisData = xs.map(Number);
    scaleType = 'linear';
  } else {
    xAxisData = xs.map(String);
    scaleType = 'point';
  }

  const series = seriesIn.map((s, i) => {
    const byX = new Map((s.data || []).map((p) => [p.x, p.y]));
    // Series data is positional — index i in the y-array maps to index i
    // in xAxisData. Build it in the same order as xs (which we just used
    // to build xAxisData) so the alignment is correct regardless of how
    // we coerced the x type.
    return {
      label: s.name || `Series ${i + 1}`,
      data:  xs.map((x) => (byX.has(x) ? toNumber(byX.get(x)) : null)),
      color: s.color || CHART_PALETTE[i % CHART_PALETTE.length],
      area:  isArea,
      stack: stacked ? 'g1' : undefined,
      showMark: xs.length <= 50,
      curve: 'monotoneX',
      valueFormatter: (v) => v == null ? '—' : yFmt(v),
    };
  });

  return (
    <Box sx={{ height }}>
      <LineChart
        xAxis={[{
          data: xAxisData,
          scaleType,
          label: xAxis?.label,
          tickLabelStyle,
          valueFormatter,
        }]}
        yAxis={[{ label: y_axisLabel(yFmt), valueFormatter: yFmt, tickLabelStyle }]}
        series={series}
        height={height}
        margin={{ left: 70, right: 20, top: 15, bottom: 40 }}
        grid={{ horizontal: true }}
        slotProps={{
          legend: { ...defaultLineLegendProps,
                    hidden: series.length === 1,
                    position: { vertical: 'top', horizontal: 'right' } },
        }}
      />
    </Box>
  );
}

function renderBar(seriesIn, xAxis, yFmt, stacked, height) {
  // Insertion-order union of x values, so the agent's intended ordering wins.
  const seen = new Set();
  const xs = [];
  for (const s of seriesIn) for (const p of (s.data || [])) {
    if (!seen.has(p.x)) { seen.add(p.x); xs.push(p.x); }
  }
  const series = seriesIn.map((s, i) => {
    const byX = new Map((s.data || []).map((p) => [p.x, p.y]));
    return {
      label: s.name || `Series ${i + 1}`,
      data:  xs.map((x) => byX.has(x) ? toNumber(byX.get(x)) : 0),
      color: s.color || CHART_PALETTE[i % CHART_PALETTE.length],
      stack: stacked ? 'g1' : undefined,
      valueFormatter: (v) => v == null ? '—' : yFmt(v),
    };
  });

  return (
    <Box sx={{ height }}>
      <BarChart
        xAxis={[{ data: xs.map(String), scaleType: 'band',
                  label: xAxis?.label, tickLabelStyle }]}
        yAxis={[{ valueFormatter: yFmt, tickLabelStyle }]}
        series={series}
        height={height}
        margin={{ left: 70, right: 20, top: 15, bottom: 40 }}
        grid={{ horizontal: true }}
        slotProps={{
          legend: { ...defaultLegendProps,
                    hidden: series.length === 1,
                    position: { vertical: 'top', horizontal: 'right' } },
        }}
      />
    </Box>
  );
}

function renderPie(seriesIn, yFmt, height) {
  const data = (seriesIn?.data || []).map((d, i) => ({
    id:    i,
    value: toNumber(d.value),
    label: d.label || `Slice ${i + 1}`,
    color: d.color || CHART_PALETTE[i % CHART_PALETTE.length],
  }));
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  return (
    <Box sx={{ height, display: 'flex', justifyContent: 'center' }}>
      <PieChart
        series={[{
          data,
          innerRadius: 60,
          outerRadius: Math.min(120, height / 2 - 20),
          paddingAngle: 1.5,
          cornerRadius: 2,
          highlightScope: { faded: 'global', highlighted: 'item' },
          valueFormatter: (item) => {
            const pct = total > 0 ? (item.value / total) * 100 : 0;
            return `${yFmt(item.value)} · ${pct.toFixed(1)}%`;
          },
        }]}
        height={height}
        margin={{ left: 0, right: 0, top: 10, bottom: 10 }}
        slotProps={{ legend: { ...defaultLegendProps,
                               position: { vertical: 'middle', horizontal: 'right' } } }}
      />
    </Box>
  );
}


// ── Helpers ──────────────────────────────────────────────────────────────────

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}


/**
 * Coerce whatever the agent emitted as a series `data` array into the
 * canonical {x, y} (or pie {label, value}) shape. Returns `[]` if nothing
 * usable is in there — the caller decides what to do with empty.
 *
 * Accepted inputs:
 *   - already-canonical:  [{x, y}, ...]   or pie  [{label, value}, ...]
 *   - tuple form:         [[x, y], ...]
 *   - raw numbers:        [12, 34, 56]    — synthesise x = "1","2","3"
 *   - pie-as-line slip:   [{label, value}] under type=line/bar → re-map
 */
function normaliseSeriesData(data, type) {
  if (!Array.isArray(data)) return [];
  if (data.length === 0) return [];

  if (type === 'pie') {
    return data.map((d, i) => {
      if (d == null) return null;
      if (typeof d === 'number') return { label: `Slice ${i + 1}`, value: d };
      // Accept {x,y} as pie too — x→label, y→value
      const label = d.label ?? d.name ?? d.x ?? `Slice ${i + 1}`;
      const value = d.value ?? d.y;
      if (value == null) return null;
      return { ...d, label, value };
    }).filter(Boolean);
  }

  // line / bar / area
  return data.map((d, i) => {
    if (d == null) return null;
    if (typeof d === 'number')  return { x: String(i + 1), y: d };
    if (Array.isArray(d) && d.length >= 2) return { x: d[0], y: d[1] };
    // Pie-shape leaking in: {label, value} → {x, y}
    if (d.x == null && d.label != null && (d.value != null || d.y != null)) {
      return { ...d, x: d.label, y: d.value ?? d.y };
    }
    if (d.x == null || (d.y == null && d.value == null)) return null;
    return { ...d, x: d.x, y: d.y ?? d.value };
  }).filter(Boolean);
}

function inferXKind(series, xAxis) {
  if (xAxis?.type) return xAxis.type;
  const sample = series.find((s) => s.data && s.data.length > 0)?.data?.[0]?.x;
  if (sample == null) return 'category';
  if (typeof sample === 'number') return 'number';
  const s = String(sample);
  if (/^\d{4}-\d{2}/.test(s) || /^\d{4}\/\d{2}/.test(s)) return 'time';
  return 'category';
}

function sortByKind(xs, kind) {
  if (kind === 'time')   return [...xs].sort((a, b) => new Date(a) - new Date(b));
  if (kind === 'number') return [...xs].sort((a, b) => Number(a) - Number(b));
  return xs;
}

function formatTimeX(xs) {
  const allMonths = xs.length > 0 && xs.every((x) => /^\d{4}-\d{2}-01$/.test(String(x)));
  return (v) => {
    if (v == null) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return allMonths
      ? d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' })
      : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };
}

// Y-axis label currently uses just the formatter; spec's y_axis.label is
// rendered by the caller (chart frame). Kept as a stub so future tweaks
// don't break ChartFromSpec.
function y_axisLabel(/* yFmt */) { return undefined; }


function SpecFallback({ raw, error }) {
  return (
    <Box sx={{ my: 1.5 }}>
      <Alert severity="warning" sx={{ fontSize: '0.78rem' }}>
        Couldn't render chart: {error}
      </Alert>
      <Box component="pre" sx={{
        fontSize: '0.72rem', mt: 0.75, px: 1.25, py: 1,
        bgcolor: 'rgba(0, 0, 0, 0.25)', color: '#e7e7ea',
        borderRadius: 1, overflowX: 'auto',
      }}>
        {typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2)}
      </Box>
    </Box>
  );
}
